import { Alert, DatePicker, Form, FormInstance, Input, Select, Checkbox } from 'antd';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../../../../contexts/UserContext';
import { FormField, FormSelect, FormTextArea } from '../../../../../components/form/FormBasic';
import { FormFieldTypeEnum } from '../../../../../enums/FormFieldTypeEnum';
import dayjs, { Dayjs } from 'dayjs';
import { referenceDataSyncService } from '../../../../../services/referenceDataSyncService';
import { TaskPriorityEnum } from '../../../../../enums/TaskPriorityEnum';

import { useNotificationContext } from '../../../../../contexts/NotificationContext';
import { getInspectorUsers } from '../../../../../api/services/userService';
import { useWatch } from 'antd/es/form/Form';
import RESOURCE_CONFIGS from '../../../../../constants/resourceConfig';
import { LegalMetroTaskTypeEnum, TaskTypeEnum } from '../../../../../enums/TaskTypeEnum';
import './CreateTaskForm.scss';
import { getDynamicTaskFields, getLegalMetroTypeOptions } from '../../../getData';
import {
  mapValidationsFromJsonToFormRules,
  ValidationsObject,
} from '../../../../../utils/mapValidationsFromJsonToFormRules';

import { getEnumOptionsWithTranslations } from '../../../../../utils/getEnumOptionsWithTranslations';
import React from 'react';
import { DynamicFieldRenderer } from '../../../../../components/form/DynamicFieldRenderer';
import { OccurrenceUnit } from '../../../../../enums/OccurenceUnitEnum';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../../../../constants/regex';
import { RoleName } from '../../../../../enums/RoleNameEnm';
import { getTypeOfEstablishmentOptions } from '../../../../../utils/typeOfEstablishmentOptions';

/* task type */
export interface TaskTypeOption {
  key: string;
  name?: string;
  id: number;
  createTime?: string | number;
  updateTime?: string | number;
}

export interface LegalMetroSubTypeOption {
  key: string;
  name?: string;
  id: number;
  taskTypeId: number;
}

/* Dynamic Fields */
export type FieldType = 'text' | 'textarea' | 'number' | 'checkbox' | 'datepicker' | 'select';

export interface DynamicTaskFieldConfig {
  name: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  // options?: { label: string; value: string}[];
  defaultValue?: any;
  validations?: ValidationsObject;
  options?: any;
  mode?: string;
  showSearch?: boolean;
  allowClear?: boolean;
}

interface AddTaskProps {
  form: FormInstance;
  // task: ITask;
  getConfirmation?: () => void;
  isEdit?: boolean;
}

const removeDuplicate = <T extends Record<string, any>>(arr: T[], property: keyof T): T[] => {
  return [...new Map(arr.map((item) => [item[property], item])).values()];
};

// Maximum number of allowed occurrences per unit
const OCCURRENCE_LIMITS: Record<OccurrenceUnit, number> = {
  [OccurrenceUnit.Days]: 60, // ~2 months
  [OccurrenceUnit.Weeks]: 52, // ~1 year
  [OccurrenceUnit.Months]: 24, // 2 years
  [OccurrenceUnit.Quarters]: 12, // 3 years
  [OccurrenceUnit.Years]: 5, // 10 years
};

function calculateMaxUntilDate(
  dueDate: Dayjs | undefined,
  freqNumber: number | undefined,
  freqUnit: OccurrenceUnit | undefined
): Dayjs | null {
  if (!dueDate || !freqNumber || !freqUnit) return null;

  const limit = OCCURRENCE_LIMITS[freqUnit];
  if (!limit) return null;

  const totalUnits = freqNumber * limit;

  switch (freqUnit) {
    case OccurrenceUnit.Days:
      return dueDate.add(totalUnits, 'day');
    case OccurrenceUnit.Weeks:
      return dueDate.add(totalUnits, 'week');
    case OccurrenceUnit.Months:
      return dueDate.add(totalUnits, 'month');
    case OccurrenceUnit.Quarters:
      return dueDate.add(totalUnits * 3, 'month');
    case OccurrenceUnit.Years:
      return dueDate.add(totalUnits, 'year');
    default:
      return null;
  }
}

const AddTaskForm: React.FC<AddTaskProps> = ({ form, getConfirmation, isEdit }) => {
  const { t } = useTranslation('task');

  const [loading, setLoading] = useState<boolean>(true);

  const [selectedTaskType, setSelectedTaskType] = useState();
  const [selectedTaskTypeId, setSelectedTaskTypeId] = useState();
  const [taskTypeOptions, setTaskTypeOptions] = useState<TaskTypeOption[]>([]);

  const [selectedLegalMetroSubType, setSelectedLegalMetroSubType] = useState();
  const [selectedLegalMetroSubTypeId, setSelectedLegalMetroSubTypeId] = useState();
  const [legalMetroSubTypeOptions, setLegalMetroSubTypeOptions] = useState<
    LegalMetroSubTypeOption[]
  >([]);

  const [fieldConfig, setFieldConfig] = useState<DynamicTaskFieldConfig[]>([]);

  const [departmentsOptions, setDepartmentsOptions] = useState<any>();
  const [municipalityOptions, setMunicipalityOptions] = useState<any>();
  const [basketsOptions, setBasketsOptions] = useState<any>();
  const [productsOptions, setProductsOptions] = useState<any>();
  const [productCategoryOptions, setProductCategoryOptions] = useState<any>();
  const [typeOfPositionOptions, setTypeOfPositionOptions] = useState<any>();
  const [establismentOptions, setEstablishmentOptions] = useState<any>();
  const [assigneeOptions, setAssigneeOptions] = useState<any>();
  const [taskTypesLoading, setTaskTypesLoading] = useState<boolean>(false);
  const [legalMetroSubTypesLoading, setLegalMetroSubTypesLoading] = useState<boolean>(false);
  const [assigneesLoading, setAssigneesLoading] = useState<boolean>(false);
  const [departmentsLoading, setDepartmentsLoading] = useState<boolean>(false);
  const [municipalitiesLoading, setMunicipalitiesLoading] = useState<boolean>(false);
  const [basketsLoading, setBasketsLoading] = useState<boolean>(false);
  const [productCategoriesLoading, setProductCategoriesLoading] = useState<boolean>(false);
  const [productsLoading, setProductsLoading] = useState<boolean>(false);
  const [typeOfPositionsLoading, setTypeOfPositionsLoading] = useState<boolean>(false);
  const [establishmentsLoading, setEstablishmentsLoading] = useState<boolean>(false);

  const [selectedBaskets, setSelectedBaskets] = useState<number[]>([]);
  const [selectedProductCategoryIds, setSelectedProductCategoryIds] = useState<number[]>([]);
  const [priorityOptions, setPriorityOptions] = useState(
    getEnumOptionsWithTranslations(TaskPriorityEnum, 'taskPriorityEnum', t)
  );

  const [optionsData, setOptionsData] = useState({ priorityLegal: priorityOptions });

  const [selectAllProducts, setSelectAllProducts] = useState<boolean>(false);
  const [recurrentTask, setRecurrentTask] = useState<boolean>(false);
  const [maxUntilDate, setMaxUntilDate] = useState<Dayjs | null>(null);
  const [enableOccursUntil, setEnableOccursUntil] = useState<boolean>(false);

  const taskTypeValue = useWatch(`taskType`, form);
  const taskTypeIdValue = useWatch(`taskTypeId`, form);
  const subTaskTypeIdValue = useWatch(`subTaskTypeId`, form);
  const legalMetroSubTypesValue = useWatch(`legalMetroSubTypes`, form);
  const marketValue = useWatch(`market`, form);
  const departmentValue = useWatch(`department`, form);
  const departmentLegalValue = useWatch(`departmentLegal`, form);
  const typeOfEstablishmentValue = useWatch(`typeOfEstablishment`, form);
  const basketIdsValue = useWatch('basketIds', form);
  const productCategoryIdsValue = useWatch('productCategoryIds', form);
  const basketIdsLegalValue = useWatch('basketIdsLegal', form);
  const productCategoryIdsLegalValue = useWatch('productCategoryIdsLegal', form);
  const municipalityValue = useWatch('municipality', form);
  const municipalityLegalValue = useWatch('municipalityLegal', form);

  const [authStatus, setAuthStatus] = useState<{ isAuthenticated: boolean; token: string | null }>({
    isAuthenticated: false,
    token: null,
  });

  const { IsAuthenticated, getUserToken } = useUserContext();
  const { errorNotification } = useNotificationContext();

  const isInitializingRef = useRef(false);

  const [editOnLoad, setEditOnLoad] = useState(false);

  const resetTaskFields = (excludedFields: string[]) => {
    const allFields = Object.keys(form.getFieldsValue());
    const fieldsToReset = allFields.filter((name) => !excludedFields.includes(name));
    form.resetFields(fieldsToReset);
    setSelectAllProducts(false);
    setRecurrentTask(false);
    setSelectedBaskets([]);
    setSelectedProductCategoryIds([]);
  };

  /* On Task Type Change */
  const onTaskTypeChange = (typeVal: any, option?: any) => {
    setSelectedTaskType(typeVal);
    setSelectedTaskTypeId(option?.id);
    form.setFieldValue('taskTypeId', option?.id);

    // Reset legal Metro Inspection selections
    form.setFieldValue('legalMetroSubTypes', undefined);
    setSelectedLegalMetroSubType(undefined);
    setSelectedLegalMetroSubTypeId(undefined);
    form.setFieldValue('subTaskTypeId', undefined);

    resetTaskFields(['taskType', 'taskTypeId']);
  };

  /* On Legal Metro Sub Task Type Change */
  const onLegalMetroSubTypeChange = (typeVal: any, option: any) => {
    setSelectedLegalMetroSubType(typeVal);
    setSelectedLegalMetroSubTypeId(option?.id);
    form.setFieldValue('subTaskTypeId', option?.id);
    resetTaskFields(['taskType', 'legalMetroSubTypes', 'taskTypeId', 'subTaskTypeId']);
  };

  const getInspectors = useCallback(async () => {
    try {
      setAssigneesLoading(true);
      const response = await getInspectorUsers({
        roles: [RoleName.INSPECTOR, RoleName.AUDITOR],
      });

      if (response?.statusText === 'SUCCESS' && response?.data?.data) {
        const users: any = response?.data?.data;
        const assigneeOptionArr = users.map((user: any) => ({
          label: user?.userName,
          value: user?.userName,
          id: user?.id,
        }));

        const uniqueAssigneeOptionArr = removeDuplicate(assigneeOptionArr, 'value');

        setAssigneeOptions(uniqueAssigneeOptionArr);
        setOptionsData((prev) => ({
          ...prev,
          assigneeLegal: uniqueAssigneeOptionArr,
        }));
        return users;
      } else {
        console.error('Failed to fetch users from backend:', response);
        errorNotification(t('noData'));
        return [];
      }
    } catch (error) {
      console.error('Failed to fetch users from backend:', error);
      errorNotification(t('userFetchError'));
      return [];
    } finally {
      setAssigneesLoading(false);
    }
  }, [errorNotification, t]);

  const getBaskets = useCallback(async () => {
    try {
      setBasketsLoading(true);
      const basketsData = await referenceDataSyncService.getBaskets();

      const basketOptionArr = basketsData.map((basket) => ({
        label: basket?.name,
        value: basket?.id,
        id: basket?.id,
      }));

      const uniqueBasketOptionArr = removeDuplicate(basketOptionArr, 'value');

      setBasketsOptions(uniqueBasketOptionArr);
    } finally {
      setBasketsLoading(false);
    }
  }, []);

  const getProductCategories = useCallback(async () => {
    try {
      setProductCategoriesLoading(true);
      const productCategoriesData = await referenceDataSyncService.getProductCategories();
      const productCategoryOptionArr = productCategoriesData.map((productCategory: any) => ({
        label: `${productCategory.mainCategory}${productCategory.category ? ` > ${productCategory.category}` : ''}`,
        value: productCategory?.id,
        id: productCategory?.id,
      }));

      const uniqueProductCategoryOptionArr = removeDuplicate(productCategoryOptionArr, 'value');
      setProductCategoryOptions(uniqueProductCategoryOptionArr);
    } catch (error) {
      setProductCategoryOptions([]);
    } finally {
      setProductCategoriesLoading(false);
    }
  }, []);

  const getLocations = useCallback(async () => {
    try {
      setDepartmentsLoading(true);
      const locationData = await referenceDataSyncService.getLocations();
      const departmentOptionArr = locationData.map((location) => ({
        label: location?.district,
        value: location?.district,
        id: location?.id,
      }));
      const uniqueDepartmentOptionArr = removeDuplicate(departmentOptionArr, 'value');

      setDepartmentsOptions(uniqueDepartmentOptionArr);
      setOptionsData((prev) => ({
        ...prev,
        departmentLegal: uniqueDepartmentOptionArr,
      }));
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  const loadMunicipalityForDepartment = useCallback(async (department: string) => {
    try {
      setMunicipalitiesLoading(true);
      const municipalityData = await referenceDataSyncService.getLocationsByDistrict(department);

      const municipalityOptionArr = municipalityData.map((municipality: any) => ({
        label: municipality.city,
        value: municipality.city,
        id: municipality.id,
      }));

      const uniqueMunicipalityOptionArr = removeDuplicate(municipalityOptionArr, 'value');

      setMunicipalityOptions(uniqueMunicipalityOptionArr);
    } finally {
      setMunicipalitiesLoading(false);
    }
  }, []);

  const getTypeOfPosition = useCallback(
    async (department?: string, municipality?: string) => {
      try {
        setTypeOfPositionsLoading(true);
        const typeOfEstablishmentOptions = await getTypeOfEstablishmentOptions(
          t,
          department,
          municipality
        );
        setTypeOfPositionOptions(typeOfEstablishmentOptions);
        setOptionsData((prev) => ({
          ...prev,
          typeOfEstablishment: typeOfEstablishmentOptions,
        }));

        const currentMarket = form.getFieldValue('market');
        if (
          currentMarket &&
          !typeOfEstablishmentOptions.some(
            (opt: any) =>
              String(opt.value).toLowerCase() === String(currentMarket).toLowerCase()
          )
        ) {
          form.setFieldValue('market', undefined);
        }
        const currentTypeOfEst = form.getFieldValue('typeOfEstablishment');
        if (
          currentTypeOfEst &&
          !typeOfEstablishmentOptions.some(
            (opt: any) =>
              String(opt.value).toLowerCase() === String(currentTypeOfEst).toLowerCase()
          )
        ) {
          form.setFieldValue('typeOfEstablishment', undefined);
        }
      } finally {
        setTypeOfPositionsLoading(false);
      }
    },
    [t, form]
  );

  const loadEstablishementsForFilters = useCallback(
    async (
      typeOfPosition?: string,
      municipalityName?: string,
      departmentName?: string
    ) => {
      try {
        setEstablishmentsLoading(true);
        let locationId: number | undefined = undefined;
        if (municipalityName) {
          const locations = await referenceDataSyncService.getLocations();
          const match = locations.find(
            (l: any) =>
              String(l.city).toLowerCase() === String(municipalityName).toLowerCase() &&
              (!departmentName ||
                String(l.district).toLowerCase() === String(departmentName).toLowerCase())
          );
          if (match) locationId = match.id;
        }
        const establishmentData =
          await referenceDataSyncService.getEstablishmentsByLocationAndPosition(
            typeOfPosition,
            locationId,
            departmentName
          );

        const establishmentArr = establishmentData.map((establishment) => ({
          label: establishment.name,
          value: establishment.name,
          id: establishment.id,
        }));
        setEstablishmentOptions(establishmentArr);
      } finally {
        setEstablishmentsLoading(false);
      }
    },
    []
  );

  const loadProductsForSelection = useCallback(async () => {
    try {
      setProductsLoading(true);
      const basketIds = selectedBaskets.map(Number).filter(Number.isFinite);
      const productCategoryIds = selectedProductCategoryIds.map(Number).filter(Number.isFinite);
      const hasProductCategoryFilter = productCategoryIds.length > 0;

      const presentationsBasedOnBaskets =
        await referenceDataSyncService.getPresentationsForFilters(basketIds);

      let filteredPresentations = presentationsBasedOnBaskets;

      if (hasProductCategoryFilter) {
        const allPresentations = await referenceDataSyncService.getPresentations();
        const presentationsByRefId = new Map(
          allPresentations.map((presentation) => [presentation.refId, presentation])
        );

        filteredPresentations = presentationsBasedOnBaskets
          .map((presentation) => presentationsByRefId.get(presentation.refId) ?? presentation)
          .filter((presentation) =>
            productCategoryIds.includes(Number(presentation.productCategoryId))
          );
      }

      const uniquePresentations = removeDuplicate(filteredPresentations, 'refId').sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );

      const presentationOptionArr = uniquePresentations.map((presentation: any) => ({
        label: presentation.name,
        value: presentation.refId,
        id: presentation.refId,
      }));

      setProductsOptions(presentationOptionArr);
      setOptionsData((prev) => ({
        ...prev,
        productLegal: presentationOptionArr,
      }));
    } catch (error) {
      console.error('Error loading products for selection:', error);
      setProductsOptions([]);
    } finally {
      setProductsLoading(false);
    }
  }, [selectedBaskets, selectedProductCategoryIds]);

  const onBasketChange = async (selectedValues: number[]) => {
    setSelectAllProducts(false);

    form.setFieldsValue({
      product: undefined,
      productRefIds: undefined,
      basketIds: selectedValues,
    });

    setSelectedBaskets(selectedValues);
  };

  const onProductCategoryChange = (selectedValues: number[]) => {
    setSelectAllProducts(false);

    form.setFieldsValue({
      product: undefined,
      productRefIds: undefined,
      productCategoryIds: selectedValues,
    });

    setSelectedProductCategoryIds(selectedValues);
  };

  const onBasketLegalChange = (selectedValues: number[]) => {
    form.setFieldsValue({
      productLegal: undefined,
      productRefId: undefined,
      basketIdsLegal: selectedValues,
    });

    setSelectedBaskets(selectedValues);
  };

  const onProductCategoryLegalChange = (selectedValues: number[]) => {
    form.setFieldsValue({
      productLegal: undefined,
      productRefId: undefined,
      productCategoryIdsLegal: selectedValues,
    });

    setSelectedProductCategoryIds(selectedValues);
  };

  const onProductChange = (selectedValue: any) => {
    form.setFieldValue('productRefIds', selectedValue);
    if (productsOptions && productsOptions.length > 0) {
      const isAllSelected = selectedValue?.length === productsOptions.length;
      setSelectAllProducts(isAllSelected);
    } else {
      setSelectAllProducts(false);
    }
  };

  const onSingleProductChange = (selectedValue: any) => {
    form.setFieldValue('productRefId', selectedValue);
    // setSelectAllProducts(false);
  };

  const onToggleRecurrentTask = (e: any) => {
    const checked = typeof e === 'boolean' ? e : e?.target?.checked;
    if (checked) {
      setRecurrentTask(checked);
    } else {
      setRecurrentTask(false);
    }
  };

  const recalcMaxDate = (val: any) => {
    const dueDate = form.getFieldValue(
      selectedTaskType === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION ? 'dueDateLegal' : 'dueDate'
    );

    const occursUntil = form.getFieldValue(
      selectedTaskType === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION
        ? 'occursUntilLegal'
        : 'occursUntil'
    );

    if (dueDate >= occursUntil || dueDate == null)
      form.setFieldValue(
        selectedTaskType === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION
          ? 'occursUntilLegal'
          : 'occursUntil',
        null
      );
    dueDate ? setEnableOccursUntil(true) : setEnableOccursUntil(false);
  };

  const disableUntilDates = (current: Dayjs) => {
    const dueDate = form.getFieldValue(
      selectedTaskType === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION ? 'dueDateLegal' : 'dueDate'
    );
    if (!current || !dueDate) return false;

    if (current.isBefore(dueDate, 'day') || current.isSame(dueDate, 'day')) return true;
    if (maxUntilDate && current.isAfter(maxUntilDate, 'day')) return true;

    return false;
  };

  const addRecurrentData = () => {
    if (selectedTaskType === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION) {
      const untilDate = form.getFieldValue('occursUntilLegal');
      const occursEveryNumber = form.getFieldValue('occursEveryNumberLegal');
      const occursEveryUnit = form.getFieldValue('occursEveryUnitLegal');

      form.setFieldsValue({
        isTaskRepeated: true,
        occursEveryNumber: occursEveryNumber,
        occursEveryUnit: occursEveryUnit,
        occursUntil: untilDate || null,
      });
    } else {
      const untilDate = form.getFieldValue('occursUntil');
      const occursEveryNumber = form.getFieldValue('occursEveryNumber');
      const occursEveryUnit = form.getFieldValue('occursEveryUnit');

      form.setFieldsValue({
        recurrent: true,
        occursEveryNumber: occursEveryNumber,
        occursEveryUnit: occursEveryUnit,
        occursUntil: untilDate || null,
      });
    }
  };

  const onToggleSelectAllProducts = (e: any) => {
    const checked = e?.target?.checked;
    setSelectAllProducts(checked);
    if (checked) {
      const allProductValues = (productsOptions || []).map((p: any) => p.value);
      form.setFieldsValue({ product: allProductValues, productRefIds: allProductValues });
    } else {
      form.setFieldsValue({ product: [], productRefIds: [] });
    }
  };

  const onDepartmemtChange = async (val: any) => {
    form.setFieldValue('municipality', undefined);
    form.setFieldValue('municipalityLegal', undefined);
    form.setFieldValue('market', undefined);
    form.setFieldValue('typeOfEstablishment', undefined);
    form.setFieldValue('typeOfMarket', undefined);
    form.setFieldValue('establishment', undefined);

    if (val) {
      try {
        await loadMunicipalityForDepartment(val.toString());
      } catch (error) {
        console.error('Error loading municipalities for department:', error);
        setMunicipalityOptions([]);
      }
    } else {
      setMunicipalityOptions([]);
    }
  };

  const handleTypeOfPosition = async (val: any) => {
    form.setFieldValue('typeOfMarket', undefined);
    form.setFieldValue('establishment', undefined);
  };

  const handleMunicipalityChange = async (val: any) => {
    form.setFieldValue('market', undefined);
    form.setFieldValue('typeOfEstablishment', undefined);
    form.setFieldValue('typeOfMarket', undefined);
    form.setFieldValue('establishment', undefined);
  };

  const onAssigneChange = (selectedValue: any, option: any) => {
    const selectedAssigneeIds = option
      .filter((assigneeOption: { value: any }) => selectedValue.includes(assigneeOption.value))
      .map((user: { id: any }) => user?.id);

    form.setFieldValue('assigneeIds', selectedAssigneeIds);
  };

  const disablePastDates = (current: any) => {
    return current && current < dayjs().startOf('day');
  };

  /* Set values on edit */
  useEffect(() => {
    if (isEdit && taskTypeValue && taskTypeIdValue && !editOnLoad) {
      setSelectedTaskType(taskTypeValue);
      setSelectedTaskTypeId(taskTypeIdValue);
      setSelectedLegalMetroSubType(legalMetroSubTypesValue);
      setSelectedLegalMetroSubTypeId(subTaskTypeIdValue);
      setEditOnLoad(false);
    }
  }, [isEdit, taskTypeValue, taskTypeIdValue, editOnLoad, legalMetroSubTypesValue, subTaskTypeIdValue]);

  useEffect(() => {
    const fetchTaskType = async () => {
      try {
        setTaskTypesLoading(true);
        setLoading(true);
        const taskTypes = await referenceDataSyncService.getTaskTypes();
        setTaskTypeOptions(taskTypes);
      } catch (error) {
      } finally {
        setTaskTypesLoading(false);
      }
    };

    fetchTaskType();
  }, []);

  /* Get sub task types ( for Legal Metrology type )  On Task Type Change */
  useEffect(() => {
    if (
      selectedTaskType &&
      selectedTaskType === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION &&
      legalMetroSubTypeOptions?.length == 0 &&
      selectedTaskTypeId
    ) {
      const fetchLegalMetroSubTypes = async () => {
        try {
          setLegalMetroSubTypesLoading(true);
          setLoading(true);
          const response = await getLegalMetroTypeOptions(selectedTaskTypeId);
          setLegalMetroSubTypeOptions(response);
        } catch (error) {
        } finally {
          setLegalMetroSubTypesLoading(false);
        }
      };

      fetchLegalMetroSubTypes();
    }
  }, [selectedTaskType, legalMetroSubTypeOptions?.length, selectedTaskTypeId]);

  /* Get fields related to the selected Legal Metrology Inspection Type */
  useEffect(() => {
    const fetchFeildConfig = async () => {
      try {
        setLoading(true);
        const response: any = await getDynamicTaskFields(selectedLegalMetroSubTypeId);
        setFieldConfig(response);
      } catch (e) {}
    };

    if (selectedLegalMetroSubTypeId !== undefined) {
      fetchFeildConfig();
    }
  }, [selectedLegalMetroSubTypeId]);

  useEffect(() => {
    const token = getUserToken();
    const isAuth = IsAuthenticated();
    setAuthStatus({ isAuthenticated: isAuth, token });
  }, [IsAuthenticated, getUserToken]);

  useEffect(() => {
    const initializeData = async () => {
      await getTypeOfPosition();
      await getInspectors();
      await getBaskets();
      await getProductCategories();
      await getLocations();
      await loadProductsForSelection(); // Load all products initially
    };

    initializeData();
  }, [getTypeOfPosition, getInspectors, getBaskets, getProductCategories, getLocations, loadProductsForSelection]);

  useEffect(() => {
    getTypeOfPosition();
  }, [t, getTypeOfPosition]);

  useEffect(() => {
    if (isEdit && !isInitializingRef.current) {
      isInitializingRef.current = true;
      const initializeForm = async () => {
        try {
          await getInspectors();

          /* For Consumer Price Inspection Type */
          if (taskTypeValue === TaskTypeEnum.CONSUMER_PRICE_INSPECTION) {
            const basketIds = form.getFieldValue('basketIds');
            if (basketIds && basketIds.length > 0) {
              setSelectedBaskets(basketIds);
            }
          }

          /* For Legal Metrology Inspection Type - Pre-Packaged Product Verification */
          if (legalMetroSubTypesValue === LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION) {
            const basketIdsLegal = form.getFieldValue('basketIdsLegal');
            if (basketIdsLegal && basketIdsLegal.length > 0) {
              setSelectedBaskets(basketIdsLegal);
            }
            const productCategoryIdsLegal = form.getFieldValue('productCategoryIdsLegal');
            if (productCategoryIdsLegal && productCategoryIdsLegal.length > 0) {
              setSelectedProductCategoryIds(productCategoryIdsLegal.map(Number));
            }
          }

          /* For Consumer Price Inspection Type and Pre-Packaged Product Verification */
          if (
            taskTypeValue === TaskTypeEnum.CONSUMER_PRICE_INSPECTION ||
            legalMetroSubTypesValue === LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION
          ) {
            await loadProductsForSelection();
          }
        } catch (error) {
          console.error('Error initializing form:', error);
        } finally {
          isInitializingRef.current = false;
        }
      };

      initializeForm();
    }
  }, [isEdit, form, taskTypeValue, legalMetroSubTypesValue, getInspectors, loadProductsForSelection]);

  useEffect(() => {
    if (isEdit && (departmentValue || departmentLegalValue)) {
      loadMunicipalityForDepartment(departmentValue || departmentLegalValue);
    }
  }, [isEdit, departmentValue, departmentLegalValue, loadMunicipalityForDepartment]);

  useEffect(() => {
    // marketValue = consumer price "type of establishment" (field name: market)
    // typeOfEstablishmentValue = legal metrology "type of establishment" (field name: typeOfEstablishment)
    const effectiveTypeOfEstablishment = marketValue || typeOfEstablishmentValue;
    const effectiveMunicipality = municipalityValue || municipalityLegalValue;
    const effectiveDepartment = departmentValue || departmentLegalValue;

    if (effectiveTypeOfEstablishment || effectiveMunicipality || effectiveDepartment) {
      loadEstablishementsForFilters(
        effectiveTypeOfEstablishment,
        effectiveMunicipality,
        effectiveDepartment
      );
    } else {
      setEstablishmentOptions([]);
    }
  }, [
    marketValue,
    typeOfEstablishmentValue,
    municipalityValue,
    municipalityLegalValue,
    departmentValue,
    departmentLegalValue,
    loadEstablishementsForFilters,
  ]);

  useEffect(() => {
    const effectiveDepartment = departmentValue || departmentLegalValue;
    const effectiveMunicipality = municipalityValue || municipalityLegalValue;
    getTypeOfPosition(effectiveDepartment, effectiveMunicipality);
  }, [
    departmentValue,
    departmentLegalValue,
    municipalityValue,
    municipalityLegalValue,
    getTypeOfPosition,
  ]);

  useEffect(() => {
    const isConsumerPrice = taskTypeValue === TaskTypeEnum.CONSUMER_PRICE_INSPECTION;
    const isPrePackagedLegal =
      taskTypeValue === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION &&
      legalMetroSubTypesValue === LegalMetroTaskTypeEnum.PRE_PACKAGED_PRODUCT_VERIFICATION;

    if (!isConsumerPrice && !isPrePackagedLegal) {
      return;
    }

    const rawBasketIds = isConsumerPrice ? basketIdsValue : basketIdsLegalValue;
    const rawProductCategoryIds = isConsumerPrice
      ? productCategoryIdsValue
      : productCategoryIdsLegalValue;

    setSelectedBaskets(Array.isArray(rawBasketIds) ? rawBasketIds.map(Number) : []);
    setSelectedProductCategoryIds(
      Array.isArray(rawProductCategoryIds) ? rawProductCategoryIds.map(Number) : []
    );
  }, [
    basketIdsValue,
    productCategoryIdsValue,
    basketIdsLegalValue,
    productCategoryIdsLegalValue,
    taskTypeValue,
    legalMetroSubTypesValue,
  ]);

  useEffect(() => {
    if (!isInitializingRef.current) {
      loadProductsForSelection();
    }
  }, [selectedBaskets, selectedProductCategoryIds, loadProductsForSelection]);

  useEffect(() => {
    if (selectAllProducts) {
      const allProductValues = (productsOptions || []).map((p: any) => p.value);
      form.setFieldsValue({ product: allProductValues, productRefIds: allProductValues });
    }
  }, [productsOptions, selectAllProducts, form]);

  useEffect(() => {
    setOptionsData((prev) => ({
      ...prev,
      establishment: establismentOptions,
    }));
  }, [establismentOptions]);

  useEffect(() => {
    setOptionsData((prev) => ({
      ...prev,
      municipalityLegal: municipalityOptions,
    }));
  }, [municipalityOptions]);

  useEffect(() => {
    setOptionsData((prev) => ({
      ...prev,
      basketsLegal: basketsOptions,
    }));
  }, [basketsOptions]);

  useEffect(() => {
    setOptionsData((prev) => ({
      ...prev,
      productCategoriesLegal: productCategoryOptions,
    }));
  }, [productCategoryOptions]);

  const productRefIds = Form.useWatch('productRefIds', form);
  const assigneeIds = Form.useWatch('assigneeIds', form);

  const fieldOnChange: any = {
    assigneeLegal: onAssigneChange,
    departmentLegal: onDepartmemtChange,
    municipalityLegal: handleMunicipalityChange,
    typeOfEstablishment: handleTypeOfPosition,
    basketsLegal: onBasketLegalChange,
    productCategoriesLegal: onProductCategoryLegalChange,
    productLegal: onSingleProductChange,
    dueDateLegal: recalcMaxDate,
    isTaskRepeated: onToggleRecurrentTask,
  };

  return (
    <>
      {!authStatus.isAuthenticated && (
        <Alert
          message="Authentication Required"
          description="You need to be logged in to sync data with the server. Please log in to enable sync functionality."
          type="warning"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}

      <Form
        layout="vertical"
        form={form}
        name="createTaskForm"
        onFinish={getConfirmation}
        className="mt-2 w-full"
        initialValues={{
          dueDate: null,
          occursEveryNumber: 1,
          occursEveryUnit: OccurrenceUnit.Days,
          dueDateLegal: null,
          occursEveryNumberLegal: 1,
          occursEveryUnitLegal: OccurrenceUnit.Days,
        }}
      >
        <div className="relative" id="taskTypeSelect">
          {/* Task Type */}
          <Form.Item
            name="taskType"
            label={t('taskType')}
            rules={[{ required: true, message: t('taskTypeRequired') }]}
          >
            <Select
              placeholder={t('taskTypePlaceholder')}
              disabled={isEdit ? true : false}
              loading={taskTypesLoading}
              allowClear={true}
              notFoundContent={t('noData')}
              options={
                taskTypeOptions &&
                taskTypeOptions?.map((type) => ({
                  label: t(`taskTypeEnum.${type?.key}`),
                  value: type?.key,
                  id: type?.id,
                }))
              }
              // options={getEnumOptionsWithTranslations(TaskTypeEnum, 'taskTypeEnum', t)}
              onChange={onTaskTypeChange}
              onMouseDown={(e) => e.stopPropagation()}
              onInputKeyDown={(e) => e.stopPropagation()}
              onOpenChange={(open) => {
                if (open) {
                  setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                  }, 0);
                }
              }}
              key={
                taskTypeValue === TaskTypeEnum.CONSUMER_PRICE_INSPECTION ? 'expanded' : 'collapsed'
              }
              getPopupContainer={(triggerNode) => {
                return (triggerNode.closest('.ant-modal-content') as HTMLElement) || document.body;
              }}
            />
          </Form.Item>
        </div>

        <Form.Item name="taskTypeId" label="task Type Id" hidden>
          <Input hidden />
        </Form.Item>

        <Form.Item name="subTaskTypeId" label="sub Task Type Id" hidden>
          <Input hidden />
        </Form.Item>

        <Form.Item name="assigneeIds" label="Assignee IDs" hidden>
          <Input hidden />
        </Form.Item>

        {/* <Form.Item noStyle shouldUpdate={(prev, current) => prev.taskType !== current.taskType}>
          {({ getFieldValue }) => {
            const taskTypeVal = getFieldValue('taskType');
            const legalMetroTypeVal = getFieldValue('legalMetroSubTypes'); */}

        {/* return taskTypeVal === TaskTypeEnum.CONSUMER_PRICE_INSPECTION ? ( */}
        {selectedTaskType === TaskTypeEnum.CONSUMER_PRICE_INSPECTION ? (
          <div className="task-field-container">
            {/* Consumer Price Inspection Type - Task Type */}
            <FormField
              name="taskName"
              label={t('taskNameLabel')}
              rules={[
                { required: true, message: t('taskNameFieldRequired') },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('taskNameSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(String(value))
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 5) {
                      return Promise.reject(t('taskNameMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('taskNameMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              placeholder={t('taskNameFieldPlaceholder')}
              disabled={false}
              type={FormFieldTypeEnum.INPUT}
              isRequired={true}
              allowLeadingTrailingSpaces={true}
              // maxLength={100}
              // minLength={3}
            />

            {/*   <FormSelect
                  name="assignee"
                  label={t('assigneeLabel')}
                  rules={[{ required: true, message: t('assigneeFieldRequired') }]}
                  placeholder={t('assigneeFieldPlaceholder')}
                  disabled={false}
                  isRequired={false}
                  allowClear={true}
                  options={inspectors}
                />  */}

            {/* Consumer Price Inspection Type - Assignee */}
            <Form.Item
              name="assignee"
              label={t('assigneeLabel')}
              rules={[{ required: true, message: t('assigneeFieldRequired') }]}
            >
              <Select
                disabled={false}
                loading={assigneesLoading}
                placeholder={t('assigneeFieldPlaceholder')}
                options={assigneeOptions}
                onChange={onAssigneChange}
                mode="multiple"
                showSearch
                notFoundContent={t('noData')}
              ></Select>
            </Form.Item>

            <div className="mb-2 flex">
              {assigneeIds?.length > 0 && (
                <a
                  className="ml-auto"
                  onClick={() => {
                    onAssigneChange([], []);
                    form.setFieldsValue({ assignee: [] });
                  }}
                >
                  {t('deselectAll')}
                </a>
              )}
            </div>

            {/* Consumer Price Inspection Type - Priority */}
            <FormSelect
              name="priority"
              label={t('priorityLabel')}
              rules={[{ required: true, message: t('priorityFieldRequired') }]}
              placeholder={t('priorityFieldPlaceholder')}
              disabled={false}
              isRequired={false}
              allowClear={true}
              // options={TaskPriorityOptions.map((p) => ({ ...p, label: t(p.label.toLowerCase()) }))}
              // options={getEnumOptionsWithTranslations(TaskPriorityEnum, 'taskPriorityEnum', t)}
              options={priorityOptions}
            />

            {/* Consumer Price Inspection Type - Department */}
            <FormSelect
              name="department"
              label={t('departmentLabel')}
              rules={[{ required: true, message: t('departmentFieldRequired') }]}
              placeholder={t('departmentFieldPlaceholder')}
              disabled={false}
              isRequired={false}
              allowClear={true}
              showSearch={true}
              options={departmentsOptions}
              loading={departmentsLoading}
              onChange={onDepartmemtChange}
            />

            {/* Consumer Price Inspection Type - Municipality */}
            <Form.Item
              name="municipality"
              label={t('municipalityLabel')}
              rules={[{ required: true, message: t('municipalityFieldRequired') }]}
            >
              <Select
                disabled={false}
                loading={municipalitiesLoading}
                placeholder={t('municipalityFieldPlaceholder')}
                options={municipalityOptions}
                allowClear={true}
                notFoundContent={t('noData')}
                showSearch
                onChange={handleMunicipalityChange}
              />
            </Form.Item>

            {/* <FormSelect
                  name="municipality"
                  label={t('municipalityLabel')}
                  rules={[{ required: true, message: t('municipalityFieldRequired') }]}
                  placeholder={t('municipalityFieldPlaceholder')}
                  disabled={false}
                  isRequired={false}
                  allowClear={true}
                  options={municipalityOptions}
                  
                /> */}

            {/* Consumer Price Inspection Type - Type of Establishment */}
            <FormSelect
              name="market"
              label={t('typeOfPositionLabel')}
              rules={[{ required: true, message: t('typeOfPositionFieldRequired') }]}
              placeholder={t('typeOfPositionFieldPlaceholder')}
              disabled={false}
              isRequired={false}
              onChange={handleTypeOfPosition}
              allowClear={true}
              options={typeOfPositionOptions}
              loading={typeOfPositionsLoading}
              showSearch={true}
            />

            {/* Consumer Price Inspection Type - Establishment */}
            <FormSelect
              name="typeOfMarket"
              options={establismentOptions}
              label={t('establishmentLabel')}
              rules={[{ required: true, message: t('establishmentFieldRequired') }]}
              disabled={false}
              placeholder={t('establishmentFieldPlaceholder')}
              isRequired={false}
              allowClear={true}
              loading={establishmentsLoading}
              showSearch={true}
            />

            {/* Consumer Price Inspection Type - Basket */}
            <Form.Item
              name="baskets"
              label={t('basketsLabel')}
              rules={[{ required: false, message: t('basketsFieldRequired') }]}
            >
              <Select
                disabled={false}
                loading={basketsLoading}
                placeholder={t('basketsFieldPlaceholder')}
                options={basketsOptions}
                mode="multiple"
                onChange={onBasketChange}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                notFoundContent={t('noData')}
              />
            </Form.Item>
            <div className="mb-2 flex">
              {selectedBaskets?.length > 0 && (
                <a
                  className="ml-auto"
                  onClick={() => {
                    onBasketChange([]);
                    form.setFieldsValue({ baskets: [] });
                  }}
                >
                  {t('deselectAll')}
                </a>
              )}
            </div>

            <Form.Item name="basketIds" label="Basket IDs" hidden>
              <Input hidden />
            </Form.Item>

            <Form.Item
              name="productCategories"
              label={t('productCategoriesLabel')}
              rules={[{ required: false, message: t('productCategoriesFieldRequired') }]}
            >
              <Select
                disabled={false}
                loading={productCategoriesLoading}
                placeholder={t('productCategoriesFieldPlaceholder')}
                options={productCategoryOptions}
                mode="multiple"
                onChange={onProductCategoryChange}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                notFoundContent={t('noData')}
              />
            </Form.Item>
            <div className="mb-2 flex">
              {selectedProductCategoryIds?.length > 0 && (
                <a
                  className="ml-auto"
                  onClick={() => {
                    onProductCategoryChange([]);
                    form.setFieldsValue({ productCategories: [] });
                  }}
                >
                  {t('deselectAll')}
                </a>
              )}
            </div>

            <Form.Item name="productCategoryIds" label="Product Category IDs" hidden>
              <Input hidden />
            </Form.Item>

            {/* Consumer Price Inspection Type - Product */}
            <Form.Item
              name="product"
              label={t('productLabel')}
              rules={[{ required: true, message: t('productFieldRequired') }]}
            >
              <Select
                disabled={false}
                loading={productsLoading}
                placeholder={t('productFieldPlaceholder')}
                options={productsOptions}
                mode="multiple"
                onChange={onProductChange}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={t('noData')}
              />
            </Form.Item>

            {/* Consumer Price Inspection Type - Select All / Deselect All product checkbox */}
            <div className="mb-2 flex">
              <Checkbox
                checked={selectAllProducts}
                onChange={onToggleSelectAllProducts}
                disabled={!productsOptions || productsOptions.length === 0}
              >
                {t('selectAll')}
              </Checkbox>

              {productRefIds?.length > 0 && (
                <a
                  className="ml-auto"
                  onClick={() => {
                    onToggleSelectAllProducts(false);
                  }}
                >
                  {t('deselectAll')}
                </a>
              )}
            </div>

            <Form.Item name="productRefIds" label="Product RefIDs" hidden>
              <Input hidden />
            </Form.Item>

            {/* Consumer Price Inspection Type - Due Date */}
            <Form.Item
              name="dueDate"
              label={t('dueDateLabel')}
              rules={[{ required: true, message: t('dueDateFieldRequired') }]}
            >
              <DatePicker
                className="w-full"
                format={RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY}
                placeholder={t('dueDateFieldPlaceholder')}
                onChange={recalcMaxDate}
                disabledDate={disablePastDates}
                inputReadOnly={true}
              />
            </Form.Item>

            {/* Consumer Price Inspection Type - Toggle Recurrent */}
            {!isEdit && (
              <Form.Item name="recurrent" valuePropName="checked" noStyle>
                <div style={{ display: 'flex' }}>
                  <Checkbox
                    checked={recurrentTask}
                    onChange={onToggleRecurrentTask}
                    disabled={!productsOptions || productsOptions.length === 0}
                  >
                    {t('repeatThisTask')}
                  </Checkbox>
                </div>
              </Form.Item>
            )}

            {/* Consumer Price Inspection Type - Task Repeat Frequency fields */}
            {recurrentTask && (
              <>
                <div className="col-on-responsive flex gap-4 md:gap-8">
                  <Form.Item label={t('occursEvery')} required style={{ marginBottom: 0 }}>
                    <div className="flex flex-row gap-2">
                      {/* Occurs Every Number */}
                      <Form.Item name="occursEveryNumber" rules={[{ required: true }]} noStyle>
                        <Select<number>
                          options={Array.from({ length: 10 }, (_, i) => ({
                            label: (i + 1).toString(),
                            value: i + 1,
                          }))}
                          // onChange={recalcMaxDate}
                          allowClear={false}
                          // showSearch
                        />
                      </Form.Item>

                      {/* Occurs Every Unit */}
                      <Form.Item name="occursEveryUnit" rules={[{ required: true }]} noStyle>
                        <Select<OccurrenceUnit>
                          options={Object.values(OccurrenceUnit).map((unit) => ({
                            label: t(unit),
                            value: unit,
                          }))}
                          // onChange={recalcMaxDate}
                          showSearch
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="occursUntil"
                    label={t('occursUntilLabel')}
                    rules={[{ required: true, message: t('occursUntilFieldRequired') }]}
                  >
                    <DatePicker
                      disabled={!enableOccursUntil}
                      className="w-full"
                      format={RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY}
                      disabledDate={disableUntilDates}
                      onChange={addRecurrentData}
                      placeholder={t('occursUntilPlaceholder')}
                      inputReadOnly={true}
                    />
                  </Form.Item>
                </div>
                {/* Show selected date */}
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const endDate = getFieldValue('occursUntil');
                    const freqNumber = getFieldValue('occursEveryNumber');
                    const unitText = getFieldValue('occursEveryUnit');

                    if (!endDate || !freqNumber || !unitText) return null;

                    const formattedDate = endDate.format(RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY);

                    let message = '';

                    freqNumber === 1
                      ? (message = `${t('taskScheduledEvery')} ${t(unitText.slice(0, -1))} ${t('until')} ${formattedDate}.`)
                      : (message = `${t('taskScheduledOnceEvery')} ${freqNumber} ${t(unitText)} ${t('until')} ${formattedDate}.`);

                    if (!message) return null;
                    return <div className="my-2 text-sm text-gray-500">{message}</div>;
                  }}
                </Form.Item>
              </>
            )}

            {/* Consumer Price Inspection Type - Description */}
            <FormTextArea
              name="description"
              label={t('descriptionLabel')}
              rules={[
                { required: false },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(t('descriptionSpaceValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_: any, value: any) => {
                    if (!value) return Promise.resolve();
                    // Allow letters, numbers, space, punctuation and special characters
                    const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                    return regex.test(String(value))
                      ? Promise.resolve()
                      : Promise.reject(t('invalidInput'));
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength < 5) {
                      return Promise.reject(t('descriptionMinValidation'));
                    }
                    return Promise.resolve();
                  },
                },
                {
                  validator: (_, value) => {
                    if (!value || value.trim() === '') {
                      return Promise.resolve();
                    }

                    const trimmedLength = value.replace(/\s/g, '').length;
                    if (trimmedLength > 100) {
                      return Promise.reject(t('descriptionMaxValidation'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              placeholder={t('descriptionFieldPlaceholder')}
              disabled={false}
              isRequired={false}
            />
          </div>
        ) : (
          selectedTaskType === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION && (
            <div className="task-field-container">
              {/* Legal Metrology Inspection Type */}
              <Form.Item
                name="legalMetroSubTypes"
                label={t('legalMetroSubTypes')}
                validateFirst
                rules={[{ required: true, message: t('legalMetroSubTypesRequired') }]}
              >
                <Select
                  placeholder={t('legalMetroSubTypesPlaceholder')}
                  disabled={isEdit ? true : false}
                  loading={legalMetroSubTypesLoading}
                  allowClear={true}
                  options={legalMetroSubTypeOptions?.map((type) => ({
                    label: t(`legalMetroTaskTypeEnum.${type?.key}`),
                    value: type?.key,
                    id: type?.id,
                  }))}
                  notFoundContent={t('noData')}
                  // options={getEnumOptionsWithTranslations(TaskTypeEnum, 'taskTypeEnum', t)}
                  onChange={onLegalMetroSubTypeChange}
                  onMouseDown={(e) => e.stopPropagation()}
                  onInputKeyDown={(e) => e.stopPropagation()}
                  getPopupContainer={(trigger) =>
                    trigger.closest('.ant-modal-content') || document.body
                  }
                />
              </Form.Item>

              <Form.Item name="productRefId" label="Product RefID" hidden>
                <Input hidden />
              </Form.Item>

              <Form.Item name="basketIdsLegal" label="Basket IDs" hidden>
                <Input hidden />
              </Form.Item>

              <Form.Item name="productCategoryIdsLegal" label="Product Category IDs" hidden>
                <Input hidden />
              </Form.Item>

              {/* Fields from JSON for Legal Metrology type */}
              {fieldConfig?.length > 0 &&
                legalMetroSubTypesValue &&
                fieldConfig?.map((field) => {
                  let fieldWithOnChange = {
                    ...field,
                    onChange: (val: any, opt?: any) => fieldOnChange[field?.name]?.(val, opt),
                    ...(field?.name === 'dueDateLegal' ? { disabledDate: disablePastDates } : {}),
                  };

                  const currentField = (
                    <Form.Item
                      key={field?.name}
                      name={field?.name}
                      label={field?.name === 'isTaskRepeated' ? undefined : t(`${field?.label}`)}
                      validateFirst
                      rules={mapValidationsFromJsonToFormRules(field?.validations, t)}
                      valuePropName={field?.type === 'checkbox' ? 'checked' : 'value'}
                    >
                      <DynamicFieldRenderer
                        field={fieldWithOnChange}
                        localOptions={optionsData}
                        t={t}
                      />
                    </Form.Item>
                  );

                  /* Repeat this task checkbox and recurrent Task fields */
                  if (field?.name === 'isTaskRepeated') {
                    if (!isEdit) {
                      return (
                        <React.Fragment key={field?.name}>
                          {/* isTaskRepeated Check box from JSON */}
                          {currentField}
                          {/* Fields for recurring tasks when isTaskRepeated is checked */}
                          {recurrentTask && (
                            <>
                              <div className="col-on-responsive flex gap-4 md:gap-8">
                                <Form.Item
                                  label={t('occursEvery')}
                                  required
                                  style={{ marginBottom: 0 }}
                                >
                                  <div className="flex flex-row gap-2">
                                    {/* Occurs Every Number */}
                                    <Form.Item
                                      name="occursEveryNumberLegal"
                                      rules={[{ required: true }]}
                                      noStyle
                                    >
                                      <Select<number>
                                        options={Array.from({ length: 10 }, (_, i) => ({
                                          label: (i + 1).toString(),
                                          value: i + 1,
                                        }))}
                                        // onChange={recalcMaxDate}
                                        allowClear={false}
                                        // showSearch
                                      />
                                    </Form.Item>

                                    {/* Occurs Every Unit */}
                                    <Form.Item
                                      name="occursEveryUnitLegal"
                                      rules={[{ required: true }]}
                                      noStyle
                                    >
                                      <Select<OccurrenceUnit>
                                        options={Object.values(OccurrenceUnit).map((unit) => ({
                                          label: t(unit),
                                          value: unit,
                                        }))}
                                        // onChange={recalcMaxDate}
                                        showSearch
                                      />
                                    </Form.Item>
                                  </div>
                                </Form.Item>

                                <Form.Item
                                  name="occursUntilLegal"
                                  label={t('occursUntilLabel')}
                                  rules={[
                                    { required: true, message: t('occursUntilFieldRequired') },
                                  ]}
                                >
                                  <DatePicker
                                    disabled={!enableOccursUntil}
                                    className="w-full"
                                    format={RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY}
                                    disabledDate={disableUntilDates}
                                    onChange={addRecurrentData}
                                    placeholder={t('occursUntilPlaceholder')}
                                  />
                                </Form.Item>
                              </div>
                              {/* Show selected date */}
                              <Form.Item shouldUpdate noStyle>
                                {({ getFieldValue }) => {
                                  const endDate = getFieldValue('occursUntilLegal');
                                  const freqNumber = getFieldValue('occursEveryNumberLegal');
                                  const unitText = getFieldValue('occursEveryUnitLegal');

                                  if (!endDate || !freqNumber || !unitText) return null;

                                  const formattedDate = endDate.format(
                                    RESOURCE_CONFIGS.DATE_TIME.DATE_ONLY
                                  );

                                  let message = '';

                                  freqNumber === 1
                                    ? (message = `${t('taskScheduledEvery')} ${t(unitText.slice(0, -1))} ${t('until')} ${formattedDate}.`)
                                    : (message = `${t('taskScheduledOnceEvery')} ${freqNumber} ${t(unitText)} ${t('until')} ${formattedDate}.`);

                                  if (!message) return null;
                                  return <div className={`text-sm text-gray-500`}>{message}</div>;
                                }}
                              </Form.Item>
                            </>
                          )}
                        </React.Fragment>
                      );
                    } else {
                      return <></>;
                    }
                  }
                  return currentField;
                })}
            </div>
          )
        )}

        {/* }}
        </Form.Item> */}
      </Form>
    </>
  );
};

export default AddTaskForm;
